import {
  FormFieldSchema,
  FormFieldType,
  TriggerCondition,
  TriggerType,
} from "@app/components/forms/types/schema";
import { Skeleton } from "@shortsworks/ui";
import { cn } from "@shortsworks/ui/lib/utils";

export const AppearanceSchema: FormFieldSchema[] = [
  {
    type: FormFieldType.RadioGroup,
    id: "shape",
    label: "위젯 모양",
    description: "위젯의 모양을 선택하세요.",
    behavior: {
      required: true,
    },
    hideIndicator: true,
    defaultValue: "story",
    appearance: {
      border: false,
    },
    optionLayout: "horizontal",
    options: [
      {
        label: (
          <div className="flex gap-1.5">
            <div className="w-10 aspect-[9/16] flex items-center">
              <Skeleton className="rounded-full w-10 h-10 bg-primary/30" />
            </div>
          </div>
        ),
        value: "story",
        description: <p className="p-2 text-center">스토리</p>,
      },
      {
        label: (
          <div className="flex gap-1.5">
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
          </div>
        ),
        value: "shorts",
        description: <p className="p-2 text-center">쇼츠</p>,
      },
      {
        label: (
          <div className="flex gap-1.5">
            <Skeleton className="w-10 aspect-[9/16] bg-primary/30" />
          </div>
        ),
        value: "embed",
        description: <p className="p-2 text-center">임베드</p>,
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Select,
    id: "type",
    label: "쇼츠가 보여지는 방식",
    description: "쇼츠가 보여지는 방식을 선택하세요.",
    placeholder: "방식 선택",
    layout: "horizontal",
    defaultValue: "group",
    options: [
      {
        label: "기본",
        value: "group",
        description: "기본 방식으로 보여집니다.",
      },
      {
        label: "단일 아이콘",
        value: "single",
        description: "단일 아이콘으로 보여집니다.",
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Switch,
    id: "useDefaultSize",
    label: "기본 크기 사용",
    defaultValue: true,
  },
  {
    type: FormFieldType.RadioGroup,
    id: "size",
    label: "위젯 크기 설정",
    description: "위젯의 크기를 설정할 수 있습니다",
    defaultValue: "basic",
    optionLayout: "horizontal",
    hideIndicator: true,
    appearance: {
      border: true,
    },
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "useDefaultSize",
          value: true,
          condition: TriggerCondition.Equal,
        },
      ],
    },
    options: [
      {
        value: "small",
        description: <p className="p-2 text-center">스몰</p>,
        label: (formValues: any) => {
          return (
            <div className={cn("p-2 flex items-center justify-center")}>
              <Skeleton
                className={cn(
                  "w-9 bg-primary/30",
                  "transition-all",
                  formValues.shape === "story"
                    ? "rounded-full aspect-square"
                    : "aspect-[9/16]",
                )}
              />
            </div>
          );
        },
      },
      {
        value: "basic",
        description: <p className="p-2 text-center">기본</p>,
        label: (formValues: any) => {
          return (
            <div className={cn("p-1 flex items-center justify-center")}>
              <Skeleton
                className={cn(
                  "w-11 bg-primary/30",
                  "transition-all",
                  formValues.shape === "story"
                    ? "rounded-full aspect-square"
                    : "aspect-[9/16]",
                )}
              />
            </div>
          );
        },
      },
      {
        value: "large",
        description: <p className="p-2 text-center">라지</p>,
        label: (formValues: any) => {
          return (
            <Skeleton
              className={cn(
                "w-[3.25rem] bg-primary/30",
                "transition-all",
                formValues.shape === "story"
                  ? "rounded-full aspect-square"
                  : "aspect-[9/16]",
              )}
            />
          );
        },
      },
    ],
  },
  {
    type: FormFieldType.Group,
    id: "widths",
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "useDefaultSize",
          value: true,
          condition: TriggerCondition.NotEqual,
        },
      ],
    },
    schemas: [
      {
        type: FormFieldType.Range,
        id: "width",
        label: "PC",
        description: "데스크탑에서 보여지는 위젯의 크기",
        layout: "horizontal",
        min: 50,
        max: 200,
        defaultValue: 100,
      },
      { type: FormFieldType.Separator },
      {
        type: FormFieldType.Range,
        id: "mobileWidth",
        label: "모바일",
        description: "모바일에서 보여지는 위젯의 크기",
        layout: "horizontal",
        min: 50,
        max: 200,
        defaultValue: 100,
      },
    ],
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Range,
    id: "customBorderRadiusStory",
    label: "위젯 테두리 둥글기",
    description: "위젯의 테두리 둥글기를 설정할 수 있습니다.",
    min: 0,
    max: 100,
    defaultValue: 50,
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "shape",
          value: "story",
          condition: TriggerCondition.Equal,
        },
      ],
    },
  },
  {
    type: FormFieldType.Range,
    id: "customBorderRadiusShorts",
    label: "위젯 테두리 둥글기",
    description: "위젯의 테두리 둥글기를 설정할 수 있습니다.",
    min: 0,
    max: 100,
    defaultValue: 10,
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "shape",
          value: "shorts",
          condition: TriggerCondition.Equal,
        },
      ],
    },
  },
  { type: FormFieldType.Separator },
  {
    type: FormFieldType.Number,
    id: "embedPadding",
    label: "임베드 모양 패딩",
    description: "임베드 모양의 위젯에 패딩을 추가합니다.",
    layout: "horizontal",
    behavior: {
      triggers: [
        {
          type: TriggerType.Show,
          field: "shape",
          value: "embed",
          condition: TriggerCondition.Equal,
        },
      ],
    },
  },
  {
    type: FormFieldType.Number,
    id: "gap",
    label: "위젯 내 간격",
    description: "위젯 내 쇼츠 아이콘 간격을 설정합니다.",
    layout: "horizontal",
    min: 10,
    max: 50,
    defaultValue: 20,
  },
];
